import { createEntityAdapter, EntityState, Update } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import { watchlistActions, watchlistApiActions } from './watchlist.actions';
import { WatchlistVM } from './watchlist.entity';

const sortByWlOrder = (a: WatchlistVM, b: WatchlistVM): number => {
  return a.wlOrder - b.wlOrder;
};

const sortDescending = (arr: WatchlistVM[]): WatchlistVM[] => {
  arr.sort(sortByWlOrder);
  return arr;
};

export const watchlistAdapter = createEntityAdapter<WatchlistVM>({
  selectId: (item) => item.isin,
  sortComparer: sortByWlOrder,
});

export interface WatchlistState extends EntityState<WatchlistVM> {
  loading: boolean;
  initialLoaded: boolean;
  error: string;
  categories: string[];
}

export const initialWatchlistState: WatchlistState = watchlistAdapter.getInitialState({
  loading: false,
  initialLoaded: false,
  error: null,
  categories: [],
});

export const watchlistFeature = createFeature({
  name: 'watchlist',
  reducer: createReducer(
    initialWatchlistState,
    on(watchlistActions.setLoading, (state, { loading }): WatchlistState => ({ ...state, loading })),
    on(
      watchlistApiActions.loadWatchlistSuccess,
      (state, { watchlist }): WatchlistState => ({
        ...watchlistAdapter.setAll(watchlist, state),
        loading: false,
        initialLoaded: true,
      }),
    ),
    on(
      watchlistApiActions.loadWatchlistFailure,
      (state): WatchlistState => ({
        ...state,
        loading: false,
      }),
    ),
    on(
      watchlistApiActions.addWatchlistItemSuccess,
      (state, { item }): WatchlistState => ({
        ...watchlistAdapter.addOne(item, state),
        error: null,
      }),
    ),
    on(
      watchlistApiActions.addWatchlistItemFailure,
      (state, { error }): WatchlistState => ({
        ...state,
        error: error?.error?.message,
      }),
    ),
    on(
      watchlistApiActions.removeWatchlistItemSuccess,
      (state, { isin }): WatchlistState => ({
        ...watchlistAdapter.removeOne(isin, state),
      }),
    ),
    on(
      watchlistActions.saveWatchlist,
      (state): WatchlistState => ({
        ...state,
        loading: true,
      }),
    ),
    on(
      watchlistApiActions.saveWatchlistSuccess,
      (state): WatchlistState => ({
        ...state,
        loading: false,
      }),
    ),
    on(
      watchlistApiActions.saveWatchlistFailure,
      (state): WatchlistState => ({
        ...state,
        loading: false,
      }),
    ),
    on(watchlistActions.reorderWatchlistItem, (state, { from, to }): WatchlistState => {
      const wlItemsToMove: Update<WatchlistVM>[] = [];
      const sortedObjects = sortDescending(Object.values(state.entities));

      const inkremeter = to > from ? -1 : 1;
      const stepsTarget = (to - from) * -1;
      for (let i = 0; i !== stepsTarget; i = i + inkremeter) {
        const wlOrderBeforeSwap = sortedObjects[to + i].wlOrder;
        sortedObjects[to + i] = { ...sortedObjects[to + i], wlOrder: sortedObjects[to + i + inkremeter].wlOrder };
        sortedObjects[to + i + inkremeter] = { ...sortedObjects[to + i + inkremeter], wlOrder: wlOrderBeforeSwap };
      }

      sortedObjects.forEach(({ isin, wlOrder }) => {
        wlItemsToMove.push({
          id: isin,
          changes: {
            wlOrder: wlOrder,
          },
        });
      });

      return {
        ...watchlistAdapter.updateMany(wlItemsToMove, state),
      };
    }),
    on(watchlistActions.updateWatchlistItemNote, (state, { isin, note }): WatchlistState => {
      return {
        ...watchlistAdapter.updateOne({ id: isin, changes: { note } }, state),
      };
    }),
    on(watchlistActions.updateWatchlistItemEntryQuote, (state, { isin, entryQuote, basedOnEntry }): WatchlistState => {
      return {
        ...watchlistAdapter.updateOne(
          { id: isin, changes: { ...calculateWatchlistItemPerformance(state.entities[isin], entryQuote, basedOnEntry) } },
          state,
        ),
      };
    }),
    on(watchlistActions.watchlistPerformanceCalculated, (state, { watchlist }): WatchlistState => {
      return {
        ...watchlistAdapter.updateMany(
          watchlist.map((i) => ({
            id: i.isin,
            changes: {
              ...i,
            },
          })),
          state,
        ),
      };
    }),
    on(watchlistActions.setWatchlistCategories, (state, { categories }): WatchlistState => {
      return {
        ...state,
        categories,
      };
    }),
  ),
  extraSelectors: ({ selectWatchlistState }) => ({
    ...watchlistAdapter.getSelectors(selectWatchlistState),
  }),
});

const calculateWatchlistItemPerformance = (item: WatchlistVM, entryQuote: number, basedOnEntry: boolean): Partial<WatchlistVM> => {
  const historicBid = item.cachedHistoricQuote?.bid ?? 0;
  const perfAbs = item.bid - entryQuote;
  const perfPct = (perfAbs / entryQuote) * 100;

  return {
    bidWhenAdded: entryQuote,
    askWhenAdded: entryQuote,
    perfAbs,
    perfPct,
    customEntryQuote: entryQuote,
    prevDayQuote: historicBid,
    calcBase: basedOnEntry ? entryQuote : historicBid,
  };
};
